"use client";

import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { Eye, EyeOff, Check, X } from "lucide-react";

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

interface FormData {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  confirm_password: string;
  referral_code: string;
  role: "customer" | "admin" | "editor";
  agree: boolean;
}

interface ApiError {
  success: false;
  message: string;
}

export default function RegisterPage() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [formData, setFormData] = useState<FormData>({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    password: "",
    confirm_password: "",
    referral_code: "",
    role: "customer",
    agree: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [apiError, setApiError] = useState<string>("");
  const [successMsg, setSuccessMsg] = useState<string>("");

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ripplesRef = useRef<{ x: number; y: number; t: number }[]>([]);
  const animRef = useRef<number | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get("error");
    if (oauthError) {
      const messages: Record<string, string> = {
        google_failed: "Google sign-up failed. Please try again.",
        account_blocked: "Your account has been blocked.",
        account_inactive: "Your account is inactive.",
        email_exists: "An account with that email already exists. Try signing in instead.",
      };
      setApiError(messages[oauthError] || "Authentication failed. Please try again.");
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const DURATION = 1800;
    const MAX_R = 160;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const draw = (now: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      ripplesRef.current = ripplesRef.current.filter((r) => now - r.t < DURATION);

      for (const r of ripplesRef.current) {
        const p = (now - r.t) / DURATION;
        const eased = 1 - Math.pow(1 - p, 3);

        ctx.beginPath();
        ctx.arc(r.x, r.y, Math.max(0, eased * MAX_R), 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(199,171,95,${(1 - p) * 0.6})`;
        ctx.lineWidth = 1.5 * (1 - p) + 0.4;
        ctx.stroke();

        if (p > 0.15) {
          const p2 = (p - 0.15) / 0.85;
          ctx.beginPath();
          ctx.arc(r.x, r.y, Math.max(0, p2 * MAX_R * 0.55), 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(199,171,95,${(1 - p2) * 0.28})`;
          ctx.lineWidth = (1 - p2) + 0.25;
          ctx.stroke();
        }
      }

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);

    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      ro.disconnect();
    };
  }, []);

  const onlyLetters = (value: string) =>
    value.replace(/[^a-zA-Z\s]/g, "").replace(/\s{2,}/g, " ");

  const onlyDigits = (value: string) =>
    value.replace(/\D/g, "").slice(0, 10);

  const onlyUpperCode = (value: string) =>
    value.replace(/[^a-zA-Z0-9-]/g, "").toUpperCase();

  const spawnRipple = (cx: number, cy: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    ripplesRef.current.push({ x: cx - rect.left, y: cy - rect.top, t: performance.now() });
  };

  const set = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
    setApiError("");
  };

  const validateStep1 = () => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!formData.first_name.trim()) e.first_name = "First name is required";
    else if (!/^[A-Za-z\s]+$/.test(formData.first_name.trim())) e.first_name = "Only letters are allowed";

    if (formData.last_name.trim() && !/^[A-Za-z\s]+$/.test(formData.last_name.trim())) {
      e.last_name = "Only letters are allowed";
    }

    if (!formData.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) e.email = "Enter a valid email";

    if (!formData.phone.trim()) e.phone = "Phone number is required";
    else if (!/^\d{10}$/.test(formData.phone)) e.phone = "Phone number must be exactly 10 digits";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const validateStep2 = () => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (!formData.password) e.password = "Password is required";
    else if (formData.password.length < 8) e.password = "Minimum 8 characters";
    if (!formData.confirm_password) e.confirm_password = "Please confirm your password";
    else if (formData.password !== formData.confirm_password) e.confirm_password = "Passwords do not match";
    if (!formData.agree) e.agree = "You must agree to the terms";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    setApiError("");
    if (validateStep1()) setStep(2);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;
    if (isLoading) return;

    setApiError("");
    setSuccessMsg("");
    setIsLoading(true);

    try {
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim() || undefined,
        email: formData.email.trim().toLowerCase(),
        phone: formData.phone.trim(),
        password: formData.password,
        role: formData.role,
      };

      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        const err = data as ApiError;

        if (response.status === 409 || err.message?.toLowerCase().includes("email already")) {
          setStep(1);
          setErrors({ email: "This email is already registered" });
        } else if (err.message?.toLowerCase().includes("password")) {
          setErrors({ password: err.message });
        } else {
          setApiError(err.message || "Registration failed. Please try again.");
        }
        return;
      }

      if (data.token) {
        localStorage.setItem("auth_token", data.token);
      }

      setSuccessMsg(`Welcome, ${data.user?.first_name || formData.first_name}! Your account has been created.`);

      setTimeout(() => {
        window.location.href = "/";
      }, 1800);
    } catch (err: unknown) {
      console.error("Registration error:", err);
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignup = () => {
    window.location.href = `${API_BASE}/api/auth/google`;
  };

  const handleClose = () => {
    router.replace("/");
  };

  const pwStrength = (() => {
    const p = formData.password;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();

  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][pwStrength];
  const strengthColor = ["", "#e53e3e", "#dd6b20", "#C7AB5F", "#38a169"][pwStrength];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&display=swap');

        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { width: 100%; height: 100%; overflow: hidden; }
        header, footer, nav { display: none !important; }

        :root {
          --navy: #051173;
          --gold: #C7AB5F;
          --white: #ffffff;
          --black: #000000;
          --error: #c0392b;
          --success: #27ae60;
        }

        .rg {
          font-family: 'Cormorant Garamond', Georgia, serif;
          position: fixed;
          inset: 0;
          width: 100%;
          height: 100vh;
          height: 100dvh;
          min-height: 100%;
          overflow: hidden;
          overscroll-behavior: none;
          z-index: 9999;
          cursor: crosshair;
          background: #000;
        }

        .bg-video {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          min-width: 100%;
          min-height: 100%;
          width: auto;
          height: auto;
          object-fit: cover;
          object-position: center;
          z-index: 0;
          pointer-events: none;
        }

        .bg-scrim {
          position: absolute; inset: 0;
          background: linear-gradient(135deg, rgba(0,0,0,0.38) 0%, rgba(0,0,0,0.18) 50%, rgba(0,0,0,0.52) 100%);
          z-index: 1;
        }

        .bg-tex {
          position: absolute; inset: 0;
          background: repeating-linear-gradient(-55deg, transparent, transparent 90px, rgba(199,171,95,0.025) 90px, rgba(199,171,95,0.025) 91px);
          z-index: 2;
        }

        .bg-canvas { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 3; }

        .bg-compass { position: absolute; top: 2.5rem; left: 2.5rem; z-index: 4; opacity: 0.22; pointer-events: none; }

        .c-tl, .c-br, .c-tr, .c-bl { position: absolute; width: 52px; height: 52px; z-index: 4; pointer-events: none; }
        .c-tl { top: 20px; left: 20px; border-top: 1.5px solid rgba(199,171,95,0.55); border-left: 1.5px solid rgba(199,171,95,0.55); }
        .c-tr { top: 20px; right: 20px; border-top: 1.5px solid rgba(199,171,95,0.55); border-right: 1.5px solid rgba(199,171,95,0.55); }
        .c-bl { bottom: 20px; left: 20px; border-bottom: 1.5px solid rgba(199,171,95,0.55); border-left: 1.5px solid rgba(199,171,95,0.55); }
        .c-br { bottom: 20px; right: 20px; border-bottom: 1.5px solid rgba(199,171,95,0.55); border-right: 1.5px solid rgba(199,171,95,0.55); }

        .bg-brand { position: absolute; bottom: 3rem; left: 3rem; z-index: 4; pointer-events: none; }
        .bg-brand-name { font-size: clamp(2rem, 3.5vw, 3.5rem); font-style: italic; font-weight: 300; color: rgba(255,255,255,0.18); line-height: 0.85; letter-spacing: -0.02em; }
        .bg-brand-name span { color: rgba(199,171,95,0.28); display: block; }
        .bg-brand-tag { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase; color: rgba(255,255,255,0.18); margin-top: 0.6rem; }

        .bg-stats { position: absolute; bottom: 3rem; right: 3rem; z-index: 4; pointer-events: none; display: flex; flex-direction: row; gap: 0.75rem; }
        .bg-stat { border: 1px solid rgba(199,171,95,0.2); background: rgba(0,0,0,0.25); backdrop-filter: blur(6px); border-radius: 6px; padding: 0.45rem 0.8rem; display: flex; flex-direction: column; align-items: flex-start; }
        .bg-stat-n { font-family: 'Cormorant Garamond', serif; font-size: 1.2rem; font-style: italic; color: var(--gold); line-height: 1; }
        .bg-stat-l { font-family: 'DM Mono', monospace; font-size: 8px; letter-spacing: 0.2em; text-transform: uppercase; color: rgba(255,255,255,0.3); margin-top: 2px; }

        .bg-badge { position: absolute; top: 2.4rem; right: 2.5rem; z-index: 4; pointer-events: none; display: inline-flex; align-items: center; gap: 0.5rem; border: 1px solid rgba(199,171,95,0.32); background: rgba(0,0,0,0.28); backdrop-filter: blur(8px); border-radius: 100px; padding: 0.3rem 0.8rem; font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase; color: var(--gold); }
        .bg-badge::before { content: ''; width: 5px; height: 5px; border-radius: 50%; background: var(--gold); flex-shrink: 0; }

        .form-wrap {
          position: absolute;
          inset: 0;
          z-index: 5;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem 1.5rem;
          overflow: hidden;
          pointer-events: none;
        }

        .fc {
          width: 100%;
          max-width: 440px;
          max-height: calc(100dvh - 3rem);
          overflow: auto;
          background: rgba(255,255,255,0.96);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border-radius: 16px;
          padding: 2.5rem 2.5rem 2rem;
          box-shadow: 0 24px 64px rgba(0,0,0,0.28), 0 0 0 1px rgba(199,171,95,0.15);
          pointer-events: all;
          opacity: 0;
          transform: translateY(28px) scale(0.98);
          transition: opacity 0.7s ease, transform 0.7s ease;
        }
        .fc.on { opacity: 1; transform: translateY(0) scale(1); }

        .fc-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 1.25rem;
        }

        .fc-header .gold-bar {
          width: 2.5rem;
          height: 2px;
          background: var(--gold);
          flex-shrink: 0;
        }

        .close-btn {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 1.5px solid rgba(5,17,115,0.2);
          background: rgba(5,17,115,0.05);
          color: #051173;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.22s, border-color 0.22s, color 0.22s, transform 0.15s;
          -webkit-tap-highlight-color: transparent;
          flex-shrink: 0;
        }

        .close-btn:hover {
          background: #051173;
          border-color: #051173;
          color: #fff;
          transform: scale(1.08);
        }

        .close-btn:active { transform: scale(0.95); }

        .alert {
          border-radius: 8px;
          padding: 0.75rem 1rem;
          margin-bottom: 1.1rem;
          font-family: 'DM Mono', monospace;
          font-size: 9.5px;
          letter-spacing: 0.08em;
          display: flex;
          align-items: flex-start;
          gap: 0.5rem;
          animation: fadeSlideDown 0.3s ease both;
        }

        @keyframes fadeSlideDown {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .alert-error {
          background: rgba(192,57,43,0.07);
          border: 1px solid rgba(192,57,43,0.25);
          color: var(--error);
        }

        .alert-success {
          background: rgba(39,174,96,0.08);
          border: 1px solid rgba(39,174,96,0.3);
          color: var(--success);
        }

        .alert-icon { flex-shrink: 0; font-size: 12px; margin-top: 1px; }

        .steps { display: flex; align-items: center; gap: 0; margin-bottom: 1.25rem; }
        .step-dot { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: 'DM Mono', monospace; font-size: 10px; font-weight: 400; transition: all 0.35s ease; flex-shrink: 0; }
        .step-dot.active { background: var(--navy); color: var(--white); border: 2px solid var(--navy); }
        .step-dot.done { background: var(--gold); color: var(--white); border: 2px solid var(--gold); }
        .step-dot.pending { background: transparent; color: #051173; border: 1.5px solid #051173; }
        .step-line { flex: 1; height: 1.5px; background: #051173; margin: 0 6px; transition: background 0.35s ease; }
        .step-line.done { background: var(--gold); }
        .step-label { font-family: 'DM Mono', monospace; font-size: 8px; letter-spacing: 0.2em; text-transform: uppercase; color: #051173; margin-top: 0.35rem; text-align: center; }
        .step-item { display: flex; flex-direction: column; align-items: center; }

        .f-eye { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.32em; text-transform: uppercase; color: #051173; margin-bottom: 0.5rem; }
        .f-title { font-size: 2.4rem; font-weight: 300; font-style: italic; color: var(--black); line-height: 0.9; letter-spacing: -0.02em; margin-bottom: 0.4rem; }
        .f-sub { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.14em; text-transform: uppercase; color: #051173; margin-bottom: 1.25rem; }

        .f2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }

        .fg { margin-bottom: 1.2rem; position: relative; }
        .fl { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.25em; text-transform: uppercase; color: var(--navy); margin-bottom: 0.5rem; display: flex; align-items: center; justify-content: space-between; }
        .fl-opt { font-size: 8px; letter-spacing: 0.1em; color: #051173; text-transform: uppercase; }
        .fiw { position: relative; }
        .fi { width: 100%; border: none; border-bottom: 1.5px solid #ddd; background: transparent; padding: 0.65rem 2.5rem 0.65rem 0; font-family: 'Cormorant Garamond', Georgia, serif; font-size: 1.05rem; color: var(--black); outline: none; transition: border-color 0.25s; letter-spacing: 0.02em; -webkit-appearance: none; border-radius: 0; }
        .fi::placeholder { color: #bbb; font-style: italic; }
        .fi:focus { border-color: var(--navy); }
        .fi.hv { border-color: var(--gold); }
        .fi.err { border-color: var(--error); }
        .fu { position: absolute; bottom: 0; left: 0; width: 0; height: 2px; background: linear-gradient(90deg, var(--navy), var(--gold)); transition: width 0.35s ease; }
        .fi:focus ~ .fu { width: 100%; }
        .fi.err ~ .fu { background: var(--error); width: 100%; }
        .ferr { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.1em; color: var(--error); margin-top: 0.35rem; display: flex; align-items: center; gap: 0.3rem; }
        .ferr::before { content: '!'; font-weight: 700; }

        .eye-btn { position: absolute; right: 0; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #051173; padding: 0.25rem; transition: color 0.2s; display: flex; align-items: center; -webkit-tap-highlight-color: transparent; min-width: 44px; min-height: 44px; justify-content: center; }
        .eye-btn:hover { color: var(--navy); }

        .pw-strength { margin-top: 0.5rem; }
        .pw-bars { display: flex; gap: 4px; margin-bottom: 0.3rem; }
        .pw-bar { height: 2px; flex: 1; border-radius: 2px; background: #eee; transition: background 0.3s; }
        .pw-lbl { font-family: 'DM Mono', monospace; font-size: 8.5px; letter-spacing: 0.15em; text-transform: uppercase; }

        .terms-row { display: flex; align-items: flex-start; gap: 0.65rem; margin-bottom: 1.25rem; }
        .terms-chk { width: 14px; height: 14px; border: 1.5px solid #051173; appearance: none; -webkit-appearance: none; cursor: pointer; position: relative; transition: border-color 0.2s, background 0.2s; flex-shrink: 0; border-radius: 2px; margin-top: 2px; }
        .terms-chk:checked { background: var(--navy); border-color: var(--navy); }
        .terms-chk:checked::after { content: ''; position: absolute; top: 1px; left: 3px; width: 5px; height: 8px; border-right: 1.5px solid white; border-bottom: 1.5px solid white; transform: rotate(40deg); }
        .terms-chk.err { border-color: var(--error); }
        .terms-lbl { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; color: rgb(0,0,0); line-height: 1.8; }
        .terms-lbl a { color: var(--navy); text-decoration: none; border-bottom: 1px solid rgba(5,17,115,0.3); transition: color 0.2s, border-color 0.2s; }
        .terms-lbl a:hover { color: var(--gold); border-color: var(--gold); }

        .sbtn-wrap { position: relative; margin-bottom: 1.1rem; }
        .sbtn-glow { position: absolute; inset: -6px; border-radius: 100px; pointer-events: none; animation: pulseRing 2.2s ease-in-out infinite; }
        @keyframes pulseRing { 0%,100% { box-shadow: 0 0 0 0 rgba(199,171,95,0); } 50% { box-shadow: 0 0 0 8px rgba(199,171,95,0.22); } }

        .sbtn { width: 100%; position: relative; overflow: hidden; background: #051173; color: var(--white); border: 2px solid var(--navy); padding: 1rem 2rem; font-family: 'DM Mono', monospace; font-size: 10px; letter-spacing: 0.28em; text-transform: uppercase; cursor: pointer; border-radius: 100px; transition: background 0.3s, border-color 0.3s, transform 0.15s, box-shadow 0.3s; display: flex; align-items: center; justify-content: center; gap: 0.6rem; -webkit-tap-highlight-color: transparent; touch-action: manipulation; }
        .sbtn::before { content: ''; position: absolute; top: 0; left: -75%; width: 55%; height: 100%; background: linear-gradient(120deg, transparent 20%, rgba(255,255,255,0.18) 50%, transparent 80%); animation: shimmer 2.6s ease-in-out infinite; border-radius: 100px; }
        @keyframes shimmer { 0% { left: -75%; opacity: 0; } 15% { opacity: 1; } 55% { left: 120%; opacity: 0; } 100% { left: 120%; opacity: 0; } }
        .sbtn .arrow { display: inline-flex; animation: arrowBounce 1.8s ease-in-out infinite; }
        @keyframes arrowBounce { 0%,100% { transform: translateX(0); } 50% { transform: translateX(4px); } }
        .sbtn:hover:not(:disabled) { background: var(--gold); border-color: var(--gold); transform: translateY(-2px); box-shadow: 0 10px 28px rgba(199,171,95,0.45); }
        .sbtn:active:not(:disabled) { transform: translateY(0); }
        .sbtn:disabled { opacity: 0.65; cursor: not-allowed; }
        .sbtn.success-state { background: var(--success); border-color: var(--success); animation: none; }
        .sbtn.success-state::before { display: none; }

        .bbtn { width: 100%; background: transparent; color: var(--navy); border: 1.5px solid rgba(5,17,115,0.25); padding: 0.85rem 2rem; font-family: 'DM Mono', monospace; font-size: 10px; letter-spacing: 0.2em; text-transform: uppercase; cursor: pointer; border-radius: 100px; transition: background 0.25s, border-color 0.25s; display: flex; align-items: center; justify-content: center; gap: 0.5rem; margin-bottom: 1.1rem; -webkit-tap-highlight-color: transparent; touch-action: manipulation; }
        .bbtn:hover { background: rgba(5,17,115,0.05); border-color: rgba(5,17,115,0.45); }

        .spin { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,0.35); border-top-color: white; border-radius: 50%; animation: sp 0.8s linear infinite; }
        @keyframes sp { to { transform: rotate(360deg); } }

        .ord { display: flex; align-items: center; gap: 1rem; margin-bottom: 1.1rem; }
        .ord::before, .ord::after { content: ''; flex: 1; height: 1px; background: #051173; }
        .ort { font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.2em; text-transform: uppercase; color: #051173; }

        /* ── Button row: Continue + Google side-by-side, equal size ── */
        .btn-row {
          display: flex;
          flex-direction: row;
          gap: 0.75rem;
          align-items: stretch;
          margin-bottom: 1.25rem;
        }

        .btn-row .sbtn-wrap {
          flex: 1;
          margin-bottom: 0;
        }

        .btn-row .sbtn-wrap .sbtn-glow {
          display: none;
        }

        .btn-row .soc {
          flex: 1;
          margin-bottom: 0;
          min-height: 50px;
        }

        .srow { display: flex; flex-direction: row; flex-wrap: nowrap; gap: 0.65rem; margin-bottom: 1.25rem; }
        .soc { flex: 1 1 0; display: flex; align-items: center; justify-content: center; gap: 0.45rem; padding: 0.7rem; border: 1.5px solid #e0e0e0; background: var(--white); border-radius: 8px; cursor: pointer; font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: var(--black); transition: background 0.25s, border-color 0.25s, color 0.25s, box-shadow 0.25s; -webkit-tap-highlight-color: transparent; touch-action: manipulation; min-height: 44px; }
        .soc:hover { background: var(--navy); border-color: var(--navy); color: var(--white); box-shadow: 0 4px 16px rgba(5,17,115,0.18); }
        .soc:hover svg path { fill: white !important; }
        .soc svg { width: 15px; height: 15px; flex-shrink: 0; }

        .rnote { text-align: center; font-family: 'DM Mono', monospace; font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: rgb(0,0,0); }
        .rnote a { color: var(--navy); text-decoration: none; border-bottom: 1px solid transparent; transition: color 0.2s, border-color 0.2s; }
        .rnote a:hover { color: var(--gold); border-color: var(--gold); }

        .slide { animation: slideIn 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) both; }
        @keyframes slideIn { from { opacity: 0; transform: translateX(24px); } to { opacity: 1; transform: translateX(0); } }

        @media (max-width: 768px) {
          .bg-brand, .bg-stats, .bg-compass { display: none; }
          .bg-badge { top: 1.2rem; right: 1.2rem; }
          .c-tl { top: 12px; left: 12px; width: 32px; height: 32px; }
          .c-tr { top: 12px; right: 12px; width: 32px; height: 32px; }
          .c-bl { bottom: 12px; left: 12px; width: 32px; height: 32px; }
          .c-br { bottom: 12px; right: 12px; width: 32px; height: 32px; }
          .form-wrap { align-items: center; padding: 1rem; }
          .fc { padding: 2rem 1.75rem 1.75rem; border-radius: 12px; width: min(94vw, 440px); }
          .f-title { font-size: 2rem; }
          .fi { font-size: 16px; }
          .sbtn { min-height: 50px; }
          .bbtn { min-height: 48px; }
          .f2 { grid-template-columns: 1fr; gap: 0; }
        }

        @media (max-width: 380px) {
          .fc { padding: 1.75rem 1.25rem 1.5rem; }
          .f-title { font-size: 1.8rem; }
          .srow { flex-direction: column; }
          .btn-row { flex-direction: column; }
          .btn-row .soc { min-height: 48px; }
        }

        @media (max-height: 720px) {
          .fc { max-height: calc(100dvh - 2rem); }
          .f-title { font-size: 2rem; }
          .fg { margin-bottom: 1rem; }
        }

        @media (prefers-reduced-motion: reduce) {
          .bg-video { animation: none; }
          .sbtn::before, .sbtn-glow, .slide, .fc, .alert { animation: none !important; transition: none !important; }
        }
      `}</style>

      <div
        className="rg"
        onMouseMove={(e) => spawnRipple(e.clientX, e.clientY)}
        onClick={(e) => spawnRipple(e.clientX, e.clientY)}
      >
        <video className="bg-video" src="/home.mp4" autoPlay loop muted playsInline />

        <div className="bg-scrim" />
        <div className="bg-tex" />
        <canvas ref={canvasRef} className="bg-canvas" />

        <div className="c-tl" />
        <div className="c-tr" />
        <div className="c-bl" />
        <div className="c-br" />

        <div className="bg-compass">
          <svg width="80" height="80" viewBox="0 0 72 72" fill="none">
            <circle cx="36" cy="36" r="34" stroke="#C7AB5F" strokeWidth="0.7" />
            <circle cx="36" cy="36" r="24" stroke="#C7AB5F" strokeWidth="0.45" />
            <circle cx="36" cy="36" r="4" stroke="#C7AB5F" strokeWidth="0.7" />
            <line x1="36" y1="2" x2="36" y2="70" stroke="#C7AB5F" strokeWidth="0.45" />
            <line x1="2" y1="36" x2="70" y2="36" stroke="#C7AB5F" strokeWidth="0.45" />
            <polygon points="36,4 38,32 36,36 34,32" fill="#C7AB5F" opacity="0.75" />
          </svg>
        </div>

        <div className="bg-stats">
          {[["10+", "Years"], ["1k+", "Clients"], ["4.9★", "Rated"]].map(([n, l]) => (
            <div className="bg-stat" key={l}>
              <div className="bg-stat-n">{n}</div>
              <div className="bg-stat-l">{l}</div>
            </div>
          ))}
        </div>

        <div className="form-wrap">
          <div className={`fc ${mounted ? "on" : ""}`}>
            <div className="fc-header">
              <div className="gold-bar" />
              <button
                className="close-btn absolute top-5 right-5 text-neutral-400 hover:text-neutral-950 hover:bg-neutral-50 p-1.5 rounded-xl transition-all"
                type="button"
                onClick={handleClose}
                aria-label="Go to home page"
              >
                <X size={14} />
              </button>
            </div>

            <div className="steps">
              <div className="step-item">
                <div className={`step-dot ${step === 1 ? "active" : "done"}`}>
                  {step > 1 ? <Check size={12} /> : "1"}
                </div>
                <div className="step-label">Profile</div>
              </div>
              <div className={`step-line ${step > 1 ? "done" : ""}`} />
              <div className="step-item">
                <div className={`step-dot ${step === 2 ? "active" : "pending"}`}>2</div>
                <div className="step-label">Security</div>
              </div>
            </div>

            <p className="f-eye">New Member</p>
            <h1 className="f-title">
              {step === 1 ? "Create Account" : successMsg ? "Welcome!" : "Set Password"}
            </h1>
            <p className="f-sub">
              {step === 1 ? "Tell us about yourself" : successMsg ? "Account created successfully" : "Secure your account"}
            </p>

            {apiError && (
              <div className="alert alert-error">
                <span className="alert-icon">✕</span>
                {apiError}
              </div>
            )}

            {successMsg && (
              <div className="alert alert-success">
                <span className="alert-icon">✓</span>
                {successMsg}
              </div>
            )}

            {step === 1 && !successMsg && (
              <div key="step1" className="slide">
                <div className="f2">
                  <div className="fg">
                    <label className="fl" htmlFor="first_name">First Name</label>
                    <div className="fiw">
                      <input
                        id="first_name"
                        type="text"
                        placeholder="Jane"
                        className={`fi ${formData.first_name ? "hv" : ""} ${errors.first_name ? "err" : ""}`}
                        value={formData.first_name}
                        onChange={(e) => set("first_name", onlyLetters(e.target.value))}
                        autoComplete="given-name"
                        inputMode="text"
                        maxLength={30}
                      />
                      <div className="fu" />
                    </div>
                    {errors.first_name && <div className="ferr">{errors.first_name}</div>}
                  </div>

                  <div className="fg">
                    <label className="fl" htmlFor="last_name">Last Name</label>
                    <div className="fiw">
                      <input
                        id="last_name"
                        type="text"
                        placeholder="Doe"
                        className={`fi ${formData.last_name ? "hv" : ""} ${errors.last_name ? "err" : ""}`}
                        value={formData.last_name}
                        onChange={(e) => set("last_name", onlyLetters(e.target.value))}
                        autoComplete="family-name"
                        inputMode="text"
                        maxLength={30}
                      />
                      <div className="fu" />
                    </div>
                    {errors.last_name && <div className="ferr">{errors.last_name}</div>}
                  </div>
                </div>

                <div className="fg">
                  <label className="fl" htmlFor="email">Email Address</label>
                  <div className="fiw">
                    <input
                      id="email"
                      type="email"
                      placeholder="your@email.com"
                      className={`fi ${formData.email ? "hv" : ""} ${errors.email ? "err" : ""}`}
                      value={formData.email}
                      onChange={(e) => set("email", e.target.value)}
                      autoComplete="email"
                    />
                    <div className="fu" />
                  </div>
                  {errors.email && <div className="ferr">{errors.email}</div>}
                </div>

                <div className="fg">
                  <label className="fl" htmlFor="phone">Phone Number</label>
                  <div className="fiw">
                    <input
                      id="phone"
                      type="tel"
                      placeholder="+91 XXX XXX XXXX"
                      className={`fi ${formData.phone ? "hv" : ""} ${errors.phone ? "err" : ""}`}
                      value={formData.phone}
                      onChange={(e) => set("phone", onlyDigits(e.target.value))}
                      inputMode="numeric"
                      pattern="[0-9]{10}"
                      maxLength={10}
                      autoComplete="tel"
                    />
                    <div className="fu" />
                  </div>
                  {errors.phone && <div className="ferr">{errors.phone}</div>}
                </div>

                {/* ── Equal-width Continue + Google buttons on one line ── */}
                <div className="btn-row">
                  <div className="sbtn-wrap">
                    <div className="sbtn-glow" />
                    <button type="button" className="sbtn" onClick={handleNext}>
                      Continue
                    </button>
                  </div>

                  <button
                    className="soc"
                    type="button"
                    onClick={handleGoogleSignup}
                    aria-label="Sign up with Google"
                  >
                    <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                    </svg>
                    Google
                  </button>
                </div>


                <p className="rnote">
                  Already a member?{" "}
                  <a href="/login">Sign in</a>
                </p>
              </div>
            )}

            {step === 2 && !successMsg && (
              <form key="step2" className="slide" onSubmit={handleSubmit}>
                <div className="fg">
                  <label className="fl" htmlFor="referral_code">
                    Referral Code <span className="fl-opt">(opt)</span>
                  </label>
                  <div className="fiw">
                    <input
                      id="referral_code"
                      type="text"
                      placeholder="MK-XXXXXX"
                      className={`fi ${formData.referral_code ? "hv" : ""}`}
                      value={formData.referral_code}
                      onChange={(e) => set("referral_code", onlyUpperCode(e.target.value))}
                      maxLength={20}
                      style={{ fontFamily: "'DM Mono', monospace", fontSize: "13px", letterSpacing: "0.1em" }}
                    />
                    <div className="fu" />
                  </div>
                </div>

                <div className="fg">
                  <label className="fl" htmlFor="password">Password</label>
                  <div className="fiw">
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Min. 8 characters"
                      className={`fi ${formData.password ? "hv" : ""} ${errors.password ? "err" : ""}`}
                      value={formData.password}
                      onChange={(e) => set("password", e.target.value)}
                      autoComplete="new-password"
                    />
                    <div className="fu" />
                    <button type="button" className="eye-btn" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {formData.password && (
                    <div className="pw-strength">
                      <div className="pw-bars">
                        {[1, 2, 3, 4].map((i) => (
                          <div key={i} className="pw-bar" style={{ background: i <= pwStrength ? strengthColor : "#eee" }} />
                        ))}
                      </div>
                      {strengthLabel && (
                        <span className="pw-lbl" style={{ color: strengthColor }}>{strengthLabel}</span>
                      )}
                    </div>
                  )}
                  {errors.password && <div className="ferr">{errors.password}</div>}
                </div>

                <div className="fg">
                  <label className="fl" htmlFor="confirm_password">Confirm Password</label>
                  <div className="fiw">
                    <input
                      id="confirm_password"
                      type={showConfirm ? "text" : "password"}
                      placeholder="Repeat password"
                      className={`fi ${formData.confirm_password ? "hv" : ""} ${errors.confirm_password ? "err" : ""}`}
                      value={formData.confirm_password}
                      onChange={(e) => set("confirm_password", e.target.value)}
                      autoComplete="new-password"
                    />
                    <div className="fu" />
                    <button type="button" className="eye-btn" onClick={() => setShowConfirm(!showConfirm)}>
                      {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                  {errors.confirm_password && <div className="ferr">{errors.confirm_password}</div>}
                </div>

                <div className="terms-row">
                  <input
                    type="checkbox"
                    id="agree"
                    className={`terms-chk ${errors.agree ? "err" : ""}`}
                    checked={formData.agree}
                    onChange={(e) => set("agree", e.target.checked)}
                  />
                  <label htmlFor="agree" className="terms-lbl">
                    I agree to the{" "}
                    <a href="/terms">Terms of Service</a>
                    {" "}and{" "}
                    <a href="/privacy">Privacy Policy</a>
                  </label>
                </div>
                {errors.agree && (
                  <div className="ferr" style={{ marginTop: "-1rem", marginBottom: "1rem" }}>
                    {errors.agree}
                  </div>
                )}

                <div className="sbtn-wrap">
                  <div className="sbtn-glow" />
                  <button type="submit" className="sbtn" disabled={isLoading}>
                    {isLoading
                      ? <><div className="spin" /> Creating Account</>
                      : <><span>Create My Account</span> <span className="arrow">→</span></>
                    }
                  </button>
                </div>

                <button type="button" className="bbtn" onClick={() => { setStep(1); setApiError(""); }} disabled={isLoading}>
                  ← Back to Profile
                </button>

                <p className="rnote">
                  Already a member?{" "}
                  <a href="/login">Sign in</a>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </>
  );
}